import { Injectable } from '@angular/core';
import { STORAGE_KEYS } from '@/app/core/const/auth.const';

/**
 * Las dos claves de la sesión pueden vivir en `sessionStorage`: con «Recordarme» sin marcar, el
 * `AuthService` guarda ahí los tokens para que la sesión se termine al cerrar la pestaña. Quien
 * los lee por este servicio —los streams de la bandeja, del laboratorio y de los pagos— tiene que
 * encontrarlos igual, así que para estas dos claves la lectura mira también ahí.
 *
 * **Y mira ahí PRIMERO.** `sessionStorage` es de esta pestaña; `localStorage`, de todo el
 * navegador. Si esta pestaña entró sin «Recordarme» y otra entró recordándola, en `localStorage`
 * está la sesión de la OTRA —que puede ser de otra persona—: leyéndolo primero, el stream de esta
 * pestaña abría con el token ajeno, y el refresco de esta pestaña quemaba el refresh token de la
 * otra (es de un solo uso). Lo de la pestaña, si está, es lo propio.
 */
const CLAVES_DE_LA_SESION: ReadonlySet<string> = new Set([
  STORAGE_KEYS.ACCESS_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
]);

/** `sessionStorage` puede no estar (modo privado, sandbox): sin él, la pestaña no guardó nada. */
function deLaPestana(clave: string): string | null {
  try {
    return sessionStorage.getItem(clave);
  } catch {
    return null;
  }
}

/**
 * LocalStorage Service
 * Wrapper around browser's localStorage to avoid magic strings and provide type safety
 */
@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  /**
   * Set item in localStorage
   * Serializes objects to JSON automatically
   */
  public setItem(key: string, value: unknown): void {
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }

  /**
   * Lo guardado en `key`, deserializado si es JSON y tal cual si no; `null` si no hay nada.
   *
   * **Quien lee dice qué espera** (`getItem<TenantSettings>(…)`, `getItem<string>(…)`): el
   * almacenamiento no puede comprobarlo, y lo que llega sin decirlo es `unknown`. El contrato era
   * `any` desde el primer commit, y un `any` es un tipo que nadie revisa: se pagó al tocar este
   * archivo para «Recordarme», porque callar la regla y arreglarla bajan el conteo lo mismo y no
   * son lo mismo.
   */
  public getItem<T = unknown>(key: string): T | null {
    try {
      const item =
        (CLAVES_DE_LA_SESION.has(key) ? deLaPestana(key) : null) ?? localStorage.getItem(key);
      if (item === null) {
        return null;
      }

      // Try to parse as JSON, if it fails return as string
      try {
        return JSON.parse(item) as T;
      } catch {
        return item as unknown as T;
      }
    } catch (error) {
      console.error(`Error getting localStorage key "${key}":`, error);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  public removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }

  /**
   * Clear all items from localStorage
   */
  public clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Check if key exists in localStorage
   */
  public hasItem(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`Error checking localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Get all keys from localStorage
   */
  public keys(): string[] {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('Error getting localStorage keys:', error);
      return [];
    }
  }
}
